import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { MegaMenu } from './MegaMenu';

const NAV_LINKS = [
  { label: 'About',           to: '/about',           testId: 'nav-about',           mega: 'about' },
  { label: 'Newsroom',        to: '/newsroom',        testId: 'nav-newsroom',        mega: 'newsroom' },
  { label: 'First Round',     to: '/first-round',     testId: 'nav-first-round' },
  { label: 'Career Hub',      to: '/career-hub',      testId: 'nav-career-hub' },
  { label: 'First Briefcase', to: '/first-briefcase', testId: 'nav-first-briefcase' },
  { label: 'Magazine',        to: '/magazine',        testId: 'nav-magazine' },
  { label: 'Podcast',         to: '/round-table',     testId: 'nav-round-table' },
  { label: 'Contact',         to: '/contact',         testId: 'nav-contact' },
];

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const closeTimer  = useRef(null);
  const headerRef   = useRef(null);
  const location    = useLocation();

  /* Close mega + mobile on route change */
  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  /* Cleanup timer on unmount */
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const openMenu     = (key) => { clearTimeout(closeTimer.current); setActiveMenu(key); };
  const scheduleClose = ()  => { closeTimer.current = setTimeout(() => setActiveMenu(null), 160); };
  const cancelClose   = ()  => { clearTimeout(closeTimer.current); };

  /* Close when keyboard focus leaves the header entirely */
  const handleHeaderBlur = (e) => {
    if (!headerRef.current?.contains(e.relatedTarget)) scheduleClose();
  };

  return (
    <>
      {/* Backdrop — tap outside to close mobile menu */}
      {mobileOpen && (
        <div
          className="op-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="mobile-menu-backdrop"
        />
      )}

      <header
        className="op-header"
        data-testid="site-header"
        ref={headerRef}
        onMouseLeave={scheduleClose}
        onMouseEnter={cancelClose}
        onBlur={handleHeaderBlur}
      >
        <div className="op-navbar container-custom">

          {/* Brand */}
          <Link to="/" className="op-brand" data-testid="site-logo">
            <span className="op-brand-mark" aria-hidden="true">OP</span>
            <span className="op-wordmark">Opportunity Post</span>
          </Link>

          {/* Desktop nav — hidden on mobile via CSS (op-nav-links media query) */}
          <nav className="op-nav-links" aria-label="Main navigation" data-testid="desktop-nav">
            {NAV_LINKS.map(({ label, to, testId, mega, ghost }) => (
              <Link
                key={to}
                to={to}
                className={ghost ? 'op-nav-ghost' : `op-nav-link${activeMenu === mega && mega ? ' op-nav-link--active' : ''}`}
                data-testid={testId}
                onMouseEnter={() => mega ? openMenu(mega) : scheduleClose()}
                onFocus={()       => mega ? openMenu(mega) : null}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Right side: Subscribe CTA */}
          <div className="hidden md:flex items-center" style={{ gap: 10, flexShrink: 0 }}>
            <Link
              to="/subscribe"
              className="op-nav-cta"
              data-testid="nav-subscribe"
            >
              Subscribe
            </Link>
          </div>

          {/* Mobile hamburger toggle */}
          <button
            className="op-mobile-toggle"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mega menu panel — desktop only */}
        {activeMenu && (
          <MegaMenu
            activeMenu={activeMenu}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          />
        )}

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <nav className="op-mobile-menu" aria-label="Mobile navigation" data-testid="mobile-menu">
            {NAV_LINKS.map(({ label, to, testId }) => (
              <Link
                key={to}
                to={to}
                data-testid={`mobile-${testId}`}
                onClick={() => setMobileOpen(false)}
              >
                {label}
              </Link>
            ))}
            <Link
              to="/subscribe"
              className="op-mobile-subscribe-btn"
              data-testid="mobile-subscribe"
              onClick={() => setMobileOpen(false)}
            >
              Subscribe
            </Link>
          </nav>
        )}
      </header>
    </>
  );
};
